import { createReducer, on } from '@ngrx/store';
import { AnagraficheActions } from './anagrafiche.actions';
import { Fornitore } from '../../core/models/fornitori.model';
import { Cliente } from '../../core/models/clienti.model';
import { Attore } from '../../core/models/attori.model';
import { AttoriQueryParams } from '../../core/models/attori.model';

export interface AnagraficheState {
  // Fornitori (cache — used by dropdowns)
  fornitori: Fornitore[];
  fornitoriLoading: boolean;
  fornitoriLoaded: boolean;
  fornitoriError: string | null;
  selectedFornitore: Fornitore | null;

  // Fornitori paginated list (used by list page)
  fornitoriList: Fornitore[];
  fornitoriListLoading: boolean;
  fornitoriListError: string | null;
  fornitoriListTotal: number;
  fornitoriListTotalPages: number;
  fornitoriListPageIndex: number;
  fornitoriListPageSize: number;
  fornitoriListLastQuery: AttoriQueryParams | undefined;

  // Clienti (cache — used by dropdowns)
  clienti: Cliente[];
  clientiLoading: boolean;
  clientiLoaded: boolean;
  clientiError: string | null;
  selectedCliente: Cliente | null;

  // Clienti paginated list (used by list page)
  clientiList: Cliente[];
  clientiListLoading: boolean;
  clientiListError: string | null;
  clientiListTotal: number;
  clientiListTotalPages: number;
  clientiListPageIndex: number;
  clientiListPageSize: number;
  clientiListLastQuery: AttoriQueryParams | undefined;

  // Operazioni
  opLoading: boolean;
  opError: string | null;
  lastCreatedAttore: Attore | null;
  createAttoreSuccessCount: number;
  createAttoreFailureCount: number;
}

export const initialState: AnagraficheState = {
  fornitori: [],
  fornitoriLoading: false,
  fornitoriLoaded: false,
  fornitoriError: null,
  selectedFornitore: null,

  fornitoriList: [],
  fornitoriListLoading: false,
  fornitoriListError: null,
  fornitoriListTotal: 0,
  fornitoriListTotalPages: 0,
  fornitoriListPageIndex: 0,
  fornitoriListPageSize: 10,
  fornitoriListLastQuery: undefined,

  clienti: [],
  clientiLoading: false,
  clientiLoaded: false,
  clientiError: null,
  selectedCliente: null,

  clientiList: [],
  clientiListLoading: false,
  clientiListError: null,
  clientiListTotal: 0,
  clientiListTotalPages: 0,
  clientiListPageIndex: 0,
  clientiListPageSize: 10,
  clientiListLastQuery: undefined,

  opLoading: false,
  opError: null,
  lastCreatedAttore: null,
  createAttoreSuccessCount: 0,
  createAttoreFailureCount: 0,
};

const toId = (v: unknown) => String(v ?? '');

export const anagraficheReducer = createReducer(
  initialState,

  // ========================================
  // ATTORE (create unificato)
  // ========================================
  on(AnagraficheActions.createAttore, (s) => ({ ...s, opLoading: true, opError: null, lastCreatedAttore: null })),
  on(AnagraficheActions.createAttoreSuccess, (s, { item }) => ({
    ...s,
    opLoading: false,
    fornitoriLoaded: false,
    clientiLoaded: false,
    lastCreatedAttore: item,
    createAttoreSuccessCount: s.createAttoreSuccessCount + 1,
  })),
  on(AnagraficheActions.createAttoreFailure, (s, { error }) => ({
    ...s,
    opLoading: false,
    opError: error,
    createAttoreFailureCount: s.createAttoreFailureCount + 1,
  })),

  // ========================================
  // FORNITORI
  // ========================================
  on(AnagraficheActions.loadFornitori, (s) => s.fornitoriLoaded ? s : { ...s, fornitoriLoading: true, fornitoriError: null }),
  on(AnagraficheActions.loadFornitoriSuccess, (s, { items }) => ({ ...s, fornitoriLoading: false, fornitoriLoaded: true, fornitori: items })),
  on(AnagraficheActions.loadFornitoriFailure, (s, { error }) => ({ ...s, fornitoriLoading: false, fornitoriError: error })),

  on(AnagraficheActions.loadFornitoriPaginated, (s, { params }) => ({ ...s, fornitoriListLoading: true, fornitoriListError: null, fornitoriListLastQuery: params })),
  on(AnagraficheActions.loadFornitoriPaginatedSuccess, (s, { items, totalElements, totalPages, pageIndex }) => ({
    ...s,
    fornitoriListLoading: false,
    fornitoriList: items,
    fornitoriListTotal: totalElements,
    fornitoriListTotalPages: totalPages,
    fornitoriListPageIndex: pageIndex,
  })),
  on(AnagraficheActions.loadFornitoriPaginatedFailure, (s, { error }) => ({ ...s, fornitoriListLoading: false, fornitoriListError: error })),

  on(AnagraficheActions.loadFornitoreDetails, (s) => ({ ...s, opLoading: true, opError: null })),
  on(AnagraficheActions.loadFornitoreDetailsSuccess, (s, { item }) => ({ ...s, opLoading: false, selectedFornitore: item })),
  on(AnagraficheActions.loadFornitoreDetailsFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(AnagraficheActions.createFornitore, (s) => ({ ...s, opLoading: true, opError: null })),
  on(AnagraficheActions.createFornitoreSuccess, (s, { item }) => ({ ...s, opLoading: false, fornitori: [item, ...s.fornitori] })),
  on(AnagraficheActions.createFornitoreFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(AnagraficheActions.updateFornitore, (s) => ({ ...s, opLoading: true, opError: null })),
  on(AnagraficheActions.updateFornitoreSuccess, (s, { item }) => ({
    ...s,
    opLoading: false,
    fornitori: s.fornitori.map((x) => (toId(x.id) === toId(item.id) ? item : x)),
    selectedFornitore: toId(s.selectedFornitore?.id) === toId(item.id) ? item : s.selectedFornitore,
  })),
  on(AnagraficheActions.updateFornitoreFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(AnagraficheActions.deleteFornitore, (s) => ({ ...s, opLoading: true, opError: null })),
  on(AnagraficheActions.deleteFornitoreSuccess, (s, { id }) => ({
    ...s,
    opLoading: false,
    fornitori: s.fornitori.filter((x) => toId(x.id) !== toId(id)),
    selectedFornitore: toId(s.selectedFornitore?.id) === toId(id) ? null : s.selectedFornitore,
  })),
  on(AnagraficheActions.deleteFornitoreFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  // ========================================
  // CLIENTI
  // ========================================
  on(AnagraficheActions.loadClienti, (s) => s.clientiLoaded ? s : { ...s, clientiLoading: true, clientiError: null }),
  on(AnagraficheActions.loadClientiSuccess, (s, { items }) => ({ ...s, clientiLoading: false, clientiLoaded: true, clienti: items })),
  on(AnagraficheActions.loadClientiFailure, (s, { error }) => ({ ...s, clientiLoading: false, clientiError: error })),

  on(AnagraficheActions.loadClientiPaginated, (s, { params }) => ({ ...s, clientiListLoading: true, clientiListError: null, clientiListLastQuery: params })),
  on(AnagraficheActions.loadClientiPaginatedSuccess, (s, { items, totalElements, totalPages, pageIndex }) => ({
    ...s,
    clientiListLoading: false,
    clientiList: items,
    clientiListTotal: totalElements,
    clientiListTotalPages: totalPages,
    clientiListPageIndex: pageIndex,
  })),
  on(AnagraficheActions.loadClientiPaginatedFailure, (s, { error }) => ({ ...s, clientiListLoading: false, clientiListError: error })),

  on(AnagraficheActions.loadClienteDetails, (s) => ({ ...s, opLoading: true, opError: null })),
  on(AnagraficheActions.loadClienteDetailsSuccess, (s, { item }) => ({ ...s, opLoading: false, selectedCliente: item })),
  on(AnagraficheActions.loadClienteDetailsFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(AnagraficheActions.createCliente, (s) => ({ ...s, opLoading: true, opError: null })),
  on(AnagraficheActions.createClienteSuccess, (s, { item }) => ({ ...s, opLoading: false, clienti: [item, ...s.clienti] })),
  on(AnagraficheActions.createClienteFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(AnagraficheActions.updateCliente, (s) => ({ ...s, opLoading: true, opError: null })),
  on(AnagraficheActions.updateClienteSuccess, (s, { item }) => ({
    ...s,
    opLoading: false,
    clienti: s.clienti.map((x) => (toId(x.id) === toId(item.id) ? item : x)),
    selectedCliente: toId(s.selectedCliente?.id) === toId(item.id) ? item : s.selectedCliente,
  })),
  on(AnagraficheActions.updateClienteFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(AnagraficheActions.deleteCliente, (s) => ({ ...s, opLoading: true, opError: null })),
  on(AnagraficheActions.deleteClienteSuccess, (s, { id }) => ({
    ...s,
    opLoading: false,
    clienti: s.clienti.filter((x) => toId(x.id) !== toId(id)),
    selectedCliente: toId(s.selectedCliente?.id) === toId(id) ? null : s.selectedCliente,
  })),
  on(AnagraficheActions.deleteClienteFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),
);
